import axiosInstance from "./axiosInstance";

/**
 * Get backlog items with filtering and pagination.
 * @param {object} params - { page, limit, search, priority, status, aiImported }
 */
export const getBacklogItems = async (params = {}) => {
  const { data } = await axiosInstance.get("api/backlog", { params });
  return data.data; // { total, page, limit, totalPages, items }
};

/**
 * Create a single backlog item manually.
 * @param {object} item - BacklogItem fields
 */
export const createBacklogItem = async (item) => {
  const { data } = await axiosInstance.post("api/backlog", item);
  return data.data;
};

/**
 * Bulk save multiple approved backlog items to the database.
 * Called after user approves AI-extracted action items.
 * @param {Array} items - Array of backlog item objects
 */
export const saveBacklogItemsBulk = async (items) => {
  const { data } = await axiosInstance.post("api/backlog/bulk", { items });
  return data.data; // Array of saved backlog items
};

/**
 * Update a backlog item by ID.
 * @param {string} id - UUID of the backlog item
 * @param {object} updates - Fields to update
 */
export const updateBacklogItem = async (id, updates) => {
  const { data } = await axiosInstance.patch(`api/backlog/${id}`, updates);
  return data.data;
};

/**
 * Approve a backlog item (status -> approved).
 * @param {string} id - UUID
 */
export const approveBacklogItem = async (id) => {
  const { data } = await axiosInstance.patch(`api/backlog/${id}/approve`);
  return data.data;
};

/**
 * Delete a single backlog item.
 * @param {string} id - UUID
 */
export const deleteBacklogItem = async (id) => {
  const { data } = await axiosInstance.delete(`api/backlog/${id}`);
  return data.data;
};

/**
 * Bulk delete multiple backlog items.
 * @param {Array<string>} ids - Array of UUIDs
 */
export const bulkDeleteBacklogItems = async (ids) => {
  const { data } = await axiosInstance.post("api/backlog/bulk-delete", { ids });
  return data.data;
};

/**
 * Move backlog items to active tasks (marks them as 'converted').
 * @param {Array<string>} ids - Array of UUIDs
 */
export const moveToActiveTasks = async (ids) => {
  const { data } = await axiosInstance.post("api/backlog/move-to-active", { ids });
  return data.data;
};

/**
 * Bulk create AI-generated tasks and directly activate them with validations.
 * @param {Array<object>} items - Array of task objects
 */
export const bulkCreateAndActivate = async (items) => {
  const { data } = await axiosInstance.post("api/backlog/bulk-create-and-activate", { items });
  return data.data;
};

/**
 * Bulk upload backlog tasks from an Excel file and/or validated task rows.
 * @param {File} file - Excel file
 * @param {Array<object>} [tasks] - Optional array of parsed/validated task objects
 */
export const bulkUploadBacklogExcel = async (file, tasks = null) => {
  const formData = new FormData();
  if (file) formData.append("file", file);
  if (tasks && Array.isArray(tasks)) {
    formData.append("tasks", JSON.stringify(tasks));
  }
  const { data } = await axiosInstance.post("api/backlog/bulk-upload", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data.data; // { totalRows, created, failed, data, errors }
};
